// Apolo IDE — PluginsView (Sprint v8 C1 redesign).
//
// shad-pro + ui-ux-pro-max + impeccable principles:
//   - Radix Tabs (4 tabs: Catalog / Installed / By Plugin / Recents)
//   - Framer Motion spring entry (stiffness 320, damping 28)
//   - Native <dialog> for backdrop + modality (works with our existing
//     accessibility wiring; Radix Dialog would duplicate that work)
//   - Skeleton loaders for the first 300ms of catalog load
//   - Empty states with invitation copy (not "Vazio")
//   - Highlight-yellow count badges per tab; accent-blue active tab indicator
//   - Sprint v8 C6 recolor: only var(--apolo-*) tokens — zero hex literals

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { motion, AnimatePresence } from 'framer-motion';
import { Command, Loader2, Package, Trash2, Upload, X, ChevronDown, Lightbulb } from 'lucide-react';
import { apiFetch } from '../lib/api.js';
// harness-library-uplift E3 — docs por skill + catálogo de tools (componentes próprios,
// pra não inflar este arquivo de 892 linhas; a auditoria pedia as duas superfícies).
import SkillDocPanel from './plugins/SkillDocPanel.jsx';
import ToolsCatalogTab from './plugins/ToolsCatalogTab.jsx';
import { TOOL_CATALOG } from '../lib/toolCatalog.js';

const RECENTS_KEY = 'apolo:plugin:skill:recents:v1';
const SPRING = { type: 'spring', stiffness: 320, damping: 28 };

function loadRecents() {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, 20) : [];
  } catch {
    return [];
  }
}

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('expected string result'));
        return;
      }
      const idx = result.indexOf(',');
      resolve(idx === -1 ? result : result.slice(idx + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function CountBadge({ count }) {
  if (!count) return null;
  return (
    <span className="ml-1.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-apolo-highlightMuted px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-apolo-highlight">
      {count}
    </span>
  );
}

function PluginCardSkeleton() {
  return (
    <li className="flex items-start gap-3 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 p-3">
      <div className="h-9 w-9 shrink-0 animate-pulse rounded-md bg-apolo-surface3" />
      <div className="flex-1 space-y-2">
        <div className="h-3.5 w-32 animate-pulse rounded bg-apolo-surface3" />
        <div className="h-3 w-full animate-pulse rounded bg-apolo-surface3" />
        <div className="h-3 w-2/3 animate-pulse rounded bg-apolo-surface3" />
      </div>
    </li>
  );
}

function PluginCard({ plugin, expanded, onToggleExpand, onToggleEnable, skills, skillsLoading, onOpenDoc }) {
  return (
    <motion.li
      layout
      whileHover={{ scale: 1.005 }}
      whileTap={{ scale: 0.997 }}
      transition={SPRING}
      className="rounded-md border border-apolo-borderSubtle bg-apolo-surface2 p-2.5 text-xs transition-colors hover:border-apolo-borderStrong focus-within:ring-2 focus-within:ring-apolo-accentRing"
    >
      <div className="flex items-start gap-2">
        <span className="text-lg leading-none" aria-hidden="true">
          {plugin.icon || '🔌'}
        </span>
        <button
          type="button"
          className="flex-1 min-w-0 text-left"
          onClick={onToggleExpand}
          aria-expanded={expanded}
        >
          <div className="flex items-center gap-2">
            <span className="truncate font-medium text-apolo-textPrimary">
              {plugin.display_name || plugin.slug}
            </span>
            {plugin.version && (
              <span className="rounded bg-apolo-surface3 px-1.5 py-0.5 text-[10px] tabular-nums text-apolo-textTertiary">
                v{plugin.version}
              </span>
            )}
            {plugin.category && (
              <span className="rounded bg-apolo-bg px-1.5 py-0.5 text-[10px] text-apolo-textTertiary">
                {plugin.category}
              </span>
            )}
          </div>
          {plugin.description && (
            <p className="mt-0.5 line-clamp-2 text-[11px] text-apolo-textSecondary">
              {plugin.description}
            </p>
          )}
        </button>
        <label
          className="inline-flex shrink-0 cursor-pointer items-center gap-1"
          title={plugin.is_enabled ? 'Desativar' : 'Ativar'}
        >
          <input
            type="checkbox"
            checked={!!plugin.is_enabled}
            onChange={(e) => onToggleEnable(plugin.id, e.target.checked)}
            aria-label={`${plugin.is_enabled ? 'Desativar' : 'Ativar'} ${plugin.display_name || plugin.slug}`}
            className="h-3.5 w-3.5 accent-apolo-accent"
          />
        </label>
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={SPRING}
            className="overflow-hidden"
          >
            <div className="mt-2 border-t border-apolo-borderSubtle pt-2">
              <div className="mb-1 text-[10px] uppercase tracking-wider text-apolo-textTertiary">
                Slash commands ({skills.length})
              </div>
              {skillsLoading ? (
                <p className="text-[11px] text-apolo-textTertiary">Carregando…</p>
              ) : skills.length === 0 ? (
                <p className="text-[11px] text-apolo-textTertiary">Nenhum comando registrado.</p>
              ) : (
                <ul className="space-y-1">
                  {skills.map((s) => (
                    <li key={s.id} className="flex items-start gap-1.5">
                      <Command size={10} aria-hidden="true" className="mt-0.5 shrink-0 text-apolo-textTertiary" />
                      <div className="min-w-0 flex-1">
                        {/* E3 — clique abre a doc da skill (playbook renderizado) */}
                        <button
                          type="button"
                          onClick={() => onOpenDoc?.(s, plugin)}
                          className="rounded text-[11px] text-apolo-accent underline-offset-2 hover:underline focus:outline-none focus-visible:ring-1 focus-visible:ring-apolo-accentRing"
                          title={`Abrir a doc de ${s.slash_command}`}
                        >
                          <code>{s.slash_command}</code>
                        </button>
                        {s.description && (
                          <span className="ml-1.5 text-[10px] text-apolo-textTertiary">— {s.description}</span>
                        )}
                      </div>
                      <span className="text-[9px] uppercase text-apolo-textTertiary">
                        {(s.handler_type || '').replace('_', ' ')}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.li>
  );
}

function EmptyState({ icon: Icon = Lightbulb, title, hint, action }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
      <Icon size={20} aria-hidden="true" className="text-apolo-textTertiary" />
      <p className="text-sm text-apolo-textSecondary">{title}</p>
      {hint && <p className="text-xs text-apolo-textTertiary">{hint}</p>}
      {action}
    </div>
  );
}

export default function PluginsView({ open, onClose }) {
  const ref = useRef(null);
  const [plugins, setPlugins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pluginName, setPluginName] = useState('');
  const [pluginSlug, setPluginSlug] = useState('');
  const [file, setFile] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [githubUrl, setGithubUrl] = useState('');
  const [githubImporting, setGithubImporting] = useState(false);
  const [notice, setNotice] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [, setCatalogLoading] = useState(false);
  const [skeletonVisible, setSkeletonVisible] = useState(false);
  const [expandedCatalogId, setExpandedCatalogId] = useState(null);
  const [pluginSkills, setPluginSkills] = useState({});
  const [pluginSkillsLoading, setPluginSkillsLoading] = useState({});
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('catalog');
  // E3 — skill selecionada pro painel de docs (null = catálogo normal).
  const [docSkill, setDocSkill] = useState(null);
  const [recents, setRecents] = useState(() => loadRecents());
  // E4 — ClawHub discovery tab (busca + trending no catálogo externo).
  const [clawhubItems, setClawhubItems] = useState([]);
  const [clawhubLoading, setClawhubLoading] = useState(false);
  const [clawhubQuery, setClawhubQuery] = useState('');
  const [clawhubDegraded, setClawhubDegraded] = useState(false);
  const [installingRepo, setInstallingRepo] = useState(null);

  useEffect(() => {
    if (open && ref.current && !ref.current.open) ref.current.showModal();
    if (!open && ref.current && ref.current.open) ref.current.close();
  }, [open]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await apiFetch('/api/plugins');
    setLoading(false);
    if (!res.ok) {
      setError(res.error || 'falha');
      return;
    }
    setPlugins(res.data.plugins ?? []);
  }, []);

  const refreshCatalog = useCallback(async () => {
    setCatalogLoading(true);
    // Skeleton appears only if fetch >300ms (impeccable: no flicker on fast networks)
    const skeletonTimer = setTimeout(() => setSkeletonVisible(true), 300);
    const res = await apiFetch('/api/plugins-catalog');
    clearTimeout(skeletonTimer);
    setSkeletonVisible(false);
    setCatalogLoading(false);
    if (res.ok) {
      setCatalog(res.data?.plugins ?? []);
    }
  }, []);

  const loadSkillsFor = useCallback(
    async (pluginId) => {
      if (pluginSkills[pluginId] || pluginSkillsLoading[pluginId]) return;
      setPluginSkillsLoading((m) => ({ ...m, [pluginId]: true }));
      const res = await apiFetch(`/api/plugins-catalog/${pluginId}/skills`);
      setPluginSkillsLoading((m) => ({ ...m, [pluginId]: false }));
      if (res.ok) {
        setPluginSkills((m) => ({ ...m, [pluginId]: res.data?.skills ?? [] }));
      }
    },
    [pluginSkills, pluginSkillsLoading],
  );

  const togglePlugin = useCallback(async (pluginId, nextEnabled) => {
    const res = await apiFetch(`/api/plugins-catalog/${pluginId}/toggle`, {
      method: 'POST',
      body: { enabled: nextEnabled },
    });
    if (res.ok) {
      setCatalog((rows) =>
        rows.map((r) => (r.id === pluginId ? { ...r, is_enabled: nextEnabled } : r)),
      );
    }
  }, []);

  useEffect(() => {
    if (open) {
      refresh();
      refreshCatalog();
      setRecents(loadRecents());
    }
  }, [open, refresh, refreshCatalog]);

  async function handleUpload(e) {
    e.preventDefault();
    setError(null);
    if (!pluginName.trim() || !pluginSlug.trim() || !file) {
      setError('Nome, slug e arquivo .zip são obrigatórios');
      return;
    }
    setUploading(true);
    let base64Zip;
    try {
      base64Zip = await fileToBase64(file);
    } catch (err) {
      setUploading(false);
      setError(err?.message || 'falha ao ler arquivo');
      return;
    }
    const res = await apiFetch('/api/plugins', {
      method: 'POST',
      body: { name: pluginName.trim(), slug: pluginSlug.trim(), base64Zip },
    });
    setUploading(false);
    if (!res.ok) {
      setError(res.error || 'falha');
      return;
    }
    setPluginName('');
    setPluginSlug('');
    setFile(null);
    setShowUploadForm(false);
    refresh();
    window.dispatchEvent(new CustomEvent('apolo:plugins-changed'));
  }

  // Import straight from a GitHub repo: the Worker fetches the zipball and runs
  // the same extract+install pipeline. Name/slug are derived server-side, so a
  // URL is all we send. Public repos work as-is; private ones use the user's
  // connected GitHub token.
  async function handleGithubImport(e) {
    if (e?.preventDefault) e.preventDefault();
    setError(null);
    setNotice(null);
    const url = githubUrl.trim();
    if (!url) {
      setError('Cole a URL de um repositório GitHub');
      return;
    }
    setGithubImporting(true);
    const res = await apiFetch('/api/plugins/from-github', {
      method: 'POST',
      body: { githubUrl: url },
    });
    setGithubImporting(false);
    if (!res.ok) {
      setError(res.error || 'falha ao importar do GitHub');
      return;
    }
    const installed = res.data?.skills_installed ?? 0;
    const skipped = res.data?.skipped?.length ?? 0;
    setNotice(
      `Importado: ${installed} skill(s)` + (skipped ? ` · ${skipped} arquivo(s) grande(s) ignorado(s)` : ''),
    );
    setGithubUrl('');
    refresh();
    window.dispatchEvent(new CustomEvent('apolo:plugins-changed'));
  }

  // E4 — load ClawHub discovery (search when q given, else trending). Degrades
  // gracefully: the backend never throws, so an outage just shows an empty list.
  const loadClawhub = useCallback(async (q) => {
    setClawhubLoading(true);
    const path = q && q.trim()
      ? `/api/plugins/clawhub/search?q=${encodeURIComponent(q.trim())}`
      : '/api/plugins/clawhub/trending';
    const res = await apiFetch(path);
    setClawhubLoading(false);
    if (!res.ok) {
      setClawhubDegraded(true);
      setClawhubItems([]);
      return;
    }
    setClawhubDegraded(res.data?.degraded === true);
    setClawhubItems(Array.isArray(res.data?.items) ? res.data.items : []);
  }, []);

  async function installFromRepo(repo) {
    if (!repo) return;
    setError(null);
    setNotice(null);
    setInstallingRepo(repo);
    const res = await apiFetch('/api/plugins/from-github', { method: 'POST', body: { githubUrl: repo } });
    setInstallingRepo(null);
    if (!res.ok) {
      setError(res.error || 'falha ao instalar do ClawHub');
      return;
    }
    setNotice(`Importado: ${res.data?.skills_installed ?? 0} skill(s)`);
    refresh();
    window.dispatchEvent(new CustomEvent('apolo:plugins-changed'));
  }

  async function handleDelete(id) {
    const res = await apiFetch(`/api/plugins/${id}`, { method: 'DELETE' });
    if (res.ok) {
      refresh();
      window.dispatchEvent(new CustomEvent('apolo:plugins-changed'));
    }
  }

  const filtered = useMemo(() => {
    if (!search.trim()) return catalog;
    const q = search.trim().toLowerCase();
    return catalog.filter(
      (p) =>
        (p.display_name || '').toLowerCase().includes(q) ||
        (p.slug || '').toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q),
    );
  }, [catalog, search]);

  const byCategory = useMemo(() => {
    const map = {};
    for (const p of filtered) {
      const k = p.category || 'general';
      if (!map[k]) map[k] = [];
      map[k].push(p);
    }
    return map;
  }, [filtered]);

  const installedCatalog = useMemo(() => filtered.filter((p) => p.is_enabled), [filtered]);

  // E3 — clique numa skill abre o painel de docs. Passa o slash COMPLETO (sem a `/`):
  // o GET /api/skills/:name tem resolver slug-aware que casa `gsap:core` → `gsap-core`;
  // cortar o namespace aqui ('core') quebrava esse match (provado no dogfood 07-14).
  const openSkillDoc = useCallback((s, plugin) => {
    const name = String(s.slash_command || '').replace(/^\//, '');
    setDocSkill({ name, label: s.display_name || name, description: s.description || plugin?.description || '' });
  }, []);

  const renderCard = (plugin) => (
    <PluginCard
      key={plugin.id}
      plugin={plugin}
      expanded={expandedCatalogId === plugin.id}
      onToggleExpand={() => {
        const next = expandedCatalogId === plugin.id ? null : plugin.id;
        setExpandedCatalogId(next);
        if (next) loadSkillsFor(plugin.id);
      }}
      onToggleEnable={togglePlugin}
      skills={pluginSkills[plugin.id] || []}
      skillsLoading={!!pluginSkillsLoading[plugin.id]}
      onOpenDoc={openSkillDoc}
    />
  );

  return (
    <dialog
      ref={ref}
      aria-labelledby="plugins-modal-title"
      onClose={onClose}
      className="rounded-2xl border border-apolo-borderSubtle bg-apolo-surface1 p-0 text-apolo-textPrimary shadow-2xl backdrop:bg-apolo-overlay backdrop:backdrop-blur-sm"
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ scale: 0.97, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.97, opacity: 0, y: 8 }}
            transition={SPRING}
            className="relative flex w-[min(56rem,100vw)] h-[100dvh] flex-col lg:h-auto lg:max-h-[88vh]"
          >
            <header
              className="flex items-center justify-between border-b border-apolo-borderSubtle p-3 lg:p-4"
              style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
            >
              <div className="flex min-w-0 items-center gap-2">
                <Package size={18} className="shrink-0 text-apolo-accent" aria-hidden="true" />
                <h2 id="plugins-modal-title" className="truncate text-base font-semibold">
                  Plugins & Skills
                </h2>
                <span className="hidden text-xs text-apolo-textTertiary tabular-nums sm:inline">
                  {catalog.length} plugins · {catalog.filter((p) => p.is_enabled).length} ativos
                  {' · '}{Object.values(pluginSkills).reduce((n, arr) => n + (arr?.length || 0), 0) || '—'} skills carregadas
                  {' · '}{Object.keys(TOOL_CATALOG).length} tools
                </span>
                {/* E3 — badge honesto: skills sem descrição visíveis no catálogo carregado */}
                {(() => {
                  const all = Object.values(pluginSkills).flat();
                  const semDesc = all.filter((s) => !s?.description).length;
                  return semDesc > 0 ? (
                    <span
                      className="hidden rounded-full border border-apolo-warning/40 px-2 py-0.5 text-[10px] tabular-nums text-apolo-warning sm:inline"
                      title={`${semDesc} skills carregadas sem descrição — backfill pendente`}
                    >
                      {semDesc} sem descrição
                    </span>
                  ) : null;
                })()}
              </div>
              <button
                type="button"
                aria-label="Fechar"
                onClick={onClose}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-apolo-borderSubtle bg-apolo-surface2 text-apolo-textSecondary transition hover:border-apolo-borderStrong hover:text-apolo-textPrimary focus:outline-none focus:ring-2 focus:ring-apolo-accentRing lg:h-9 lg:w-9"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </header>

            <Tabs.Root
              value={tab}
              onValueChange={(v) => {
                setTab(v);
                if (v === 'discover' && clawhubItems.length === 0 && !clawhubLoading) loadClawhub('');
              }}
              className="flex flex-1 flex-col overflow-hidden"
            >
              {/* Mobile: tabs em row scroll-x, search/upload em row separada abaixo.
                  Desktop ≥lg: tudo inline na mesma row (layout original preservado). */}
              <div className="flex shrink-0 flex-col gap-2 border-b border-apolo-borderSubtle lg:flex-row lg:items-center lg:gap-0">
                <Tabs.List
                  aria-label="Plugin tabs"
                  className="no-scrollbar flex shrink-0 items-center gap-1 overflow-x-auto px-3 lg:px-4"
                >
                  {[
                    { v: 'catalog', label: 'Catalog', count: catalog.length },
                    { v: 'installed', label: 'Installed', count: catalog.filter((p) => p.is_enabled).length },
                    { v: 'by-plugin', label: 'By Plugin' },
                    { v: 'tools', label: 'Tools', count: Object.keys(TOOL_CATALOG).length },
                    { v: 'discover', label: 'Descobrir' },
                    { v: 'recents', label: 'Recently Used', count: recents.length },
                  ].map((t) => (
                    <Tabs.Trigger
                      key={t.v}
                      value={t.v}
                      className="group relative inline-flex shrink-0 items-center gap-1 whitespace-nowrap px-3 py-3 text-xs font-medium text-apolo-textSecondary transition-colors hover:text-apolo-textPrimary data-[state=active]:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing lg:py-2.5"
                    >
                      {t.label}
                      <CountBadge count={t.count} />
                      <span className="pointer-events-none absolute inset-x-2 -bottom-px h-0.5 origin-left scale-x-0 rounded-full bg-apolo-accent transition-transform group-data-[state=active]:scale-x-100" />
                    </Tabs.Trigger>
                  ))}
                </Tabs.List>
                <div className="flex items-center gap-2 px-3 pb-2 lg:ml-auto lg:px-4 lg:pb-0">
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar plugin..."
                    aria-label="Buscar plugin"
                    className="min-w-0 flex-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-2 text-sm text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:border-apolo-accent focus:outline-none focus-visible:ring-1 focus-visible:ring-apolo-accentRing lg:flex-none lg:py-1 lg:text-xs"
                  />
                  <button
                    type="button"
                    onClick={() => setShowUploadForm((v) => !v)}
                    title="Instalar plugin custom (.zip)"
                    aria-expanded={showUploadForm}
                    className="inline-flex h-11 shrink-0 items-center gap-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-3 text-xs text-apolo-textSecondary transition hover:border-apolo-borderStrong hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing lg:h-auto lg:px-2 lg:py-1"
                  >
                    <Upload size={11} aria-hidden="true" />
                    <span className="hidden sm:inline">Upload</span>
                    <ChevronDown
                      size={11}
                      aria-hidden="true"
                      className={`transition-transform ${showUploadForm ? 'rotate-180' : ''}`}
                    />
                  </button>
                </div>
              </div>

              {/* Sprint v8.3 — removido motion.form com height:auto (Framer's
                  auto-height trick falha em layouts complex e form ficava
                  invisível). Render direto + fade-in CSS é robusto e Pedro
                  vê todos os campos sempre. */}
              {showUploadForm && (
                <form
                  onSubmit={handleUpload}
                  className="border-b border-apolo-borderSubtle bg-apolo-surface2 animate-in fade-in slide-in-from-top-2 duration-200"
                >
                  <div className="flex flex-col gap-2.5 p-3 text-sm">
                    <div className="flex items-center justify-between">
                      <h3 className="text-[10px] uppercase tracking-wider text-apolo-textTertiary">
                        Instalar plugin custom (.zip · até 100 MB)
                      </h3>
                      <a
                        href="https://github.com/garrytan/gstack/archive/refs/heads/main.zip"
                        target="_blank"
                        rel="noreferrer"
                        className="text-[10px] text-apolo-accent transition hover:underline"
                      >
                        gstack ↗
                      </a>
                    </div>

                    {/* Importar do GitHub — cola a URL e o Worker baixa + instala. */}
                    <div className="flex flex-col gap-1.5 rounded-md border border-apolo-borderSubtle bg-apolo-surface1/60 p-2.5">
                      <label
                        htmlFor="gh-import-url"
                        className="text-[10px] uppercase tracking-wider text-apolo-textTertiary"
                      >
                        Importar do GitHub
                      </label>
                      <div className="flex flex-col gap-2 lg:flex-row">
                        <input
                          id="gh-import-url"
                          type="url"
                          inputMode="url"
                          value={githubUrl}
                          onChange={(e) => setGithubUrl(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleGithubImport(e);
                          }}
                          placeholder="github.com/owner/repo"
                          className="min-w-0 flex-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface1 px-2 py-2 text-sm text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:border-apolo-accent focus:outline-none lg:py-1.5 lg:text-xs"
                        />
                        <button
                          type="button"
                          onClick={handleGithubImport}
                          disabled={githubImporting || !githubUrl.trim()}
                          className="inline-flex h-11 shrink-0 items-center justify-center gap-1 rounded-md border border-apolo-accent/50 bg-apolo-accent/10 px-3 text-sm font-medium text-apolo-accent transition hover:bg-apolo-accent/20 focus:outline-none focus:ring-2 focus:ring-apolo-accentRing disabled:cursor-not-allowed disabled:opacity-40 lg:h-auto lg:py-1.5 lg:text-xs"
                        >
                          {githubImporting ? (
                            <Loader2 size={11} className="animate-spin" aria-hidden="true" />
                          ) : (
                            <Upload size={11} aria-hidden="true" />
                          )}
                          Importar
                        </button>
                      </div>
                      <p className="text-[10px] text-apolo-textTertiary">
                        Repo público (ou privado, se você conectou o GitHub). Skills e comandos entram;
                        assets pesados são ignorados.
                      </p>
                    </div>

                    <div className="flex items-center gap-2 py-0.5">
                      <span className="h-px flex-1 bg-apolo-borderSubtle" />
                      <span className="text-[10px] uppercase tracking-wider text-apolo-textTertiary">
                        ou envie um .zip
                      </span>
                      <span className="h-px flex-1 bg-apolo-borderSubtle" />
                    </div>

                    <div className="flex flex-col gap-2 lg:flex-row">
                      <input
                        type="text"
                        value={pluginName}
                        onChange={(e) => setPluginName(e.target.value)}
                        placeholder="Nome do plugin"
                        className="flex-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface1 px-2 py-2 text-sm text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:border-apolo-accent focus:outline-none lg:py-1.5 lg:text-xs"
                      />
                      <input
                        type="text"
                        value={pluginSlug}
                        onChange={(e) => setPluginSlug(e.target.value)}
                        placeholder="slug-do-plugin"
                        className="rounded-md border border-apolo-borderSubtle bg-apolo-surface1 px-2 py-2 text-sm text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:border-apolo-accent focus:outline-none lg:w-40 lg:py-1.5 lg:text-xs"
                      />
                    </div>
                    <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-apolo-borderSubtle bg-apolo-surface1/60 px-3 py-2 text-xs text-apolo-textSecondary transition hover:border-apolo-accent/60 hover:bg-apolo-surface1">
                      <Upload size={12} aria-hidden="true" className="text-apolo-textTertiary" />
                      <span className="flex-1 truncate">
                        {file ? file.name : 'Selecionar arquivo .zip'}
                      </span>
                      {file && (
                        <span className="text-[10px] text-apolo-textTertiary tabular-nums">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </span>
                      )}
                      <input
                        type="file"
                        accept=".zip,application/zip"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        className="hidden"
                      />
                    </label>
                    <div className="flex flex-col items-stretch gap-2 lg:flex-row lg:items-center lg:justify-between">
                      <p className="text-[10px] text-apolo-textTertiary">
                        Nome + slug + .zip — depois é só clicar em <strong className="text-apolo-textSecondary">Enviar</strong>.
                      </p>
                      <button
                        type="submit"
                        disabled={uploading || !file || !pluginName || !pluginSlug}
                        className="inline-flex h-11 w-full items-center justify-center gap-1 rounded-md bg-apolo-accent px-3 text-sm font-medium text-apolo-bg transition hover:bg-apolo-accentHover focus:outline-none focus:ring-2 focus:ring-apolo-accentRing disabled:cursor-not-allowed disabled:opacity-40 lg:h-auto lg:w-auto lg:py-1.5 lg:text-xs"
                      >
                        {uploading ? (
                          <Loader2 size={11} className="animate-spin" aria-hidden="true" />
                        ) : (
                          <Upload size={11} aria-hidden="true" />
                        )}
                        Enviar
                      </button>
                    </div>
                  </div>
                </form>
              )}

              {error && (
                <div
                  role="alert"
                  className="mx-4 mt-3 rounded-md border border-apolo-error/40 bg-apolo-error/10 px-3 py-2 text-xs text-apolo-error"
                >
                  {error}
                </div>
              )}

              {notice && (
                <div
                  role="status"
                  className="mx-4 mt-3 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-3 py-2 text-xs text-apolo-textSecondary"
                >
                  {notice}
                </div>
              )}

              {/* E3 — aba Tools: catálogo das ferramentas do harness (fonte: toolCatalog.js) */}
              <Tabs.Content
                value="tools"
                className="flex-1 overflow-hidden focus:outline-none data-[state=active]:flex data-[state=active]:flex-col"
              >
                <div className="flex-1 overflow-y-auto">
                  <ToolsCatalogTab search={search} />
                </div>
              </Tabs.Content>

              <Tabs.Content
                value="catalog"
                className="flex-1 overflow-hidden focus:outline-none data-[state=active]:flex data-[state=active]:flex-col"
              >
                <div className="flex-1 overflow-y-auto">
                    {skeletonVisible && catalog.length === 0 ? (
                      <ul className="space-y-2 p-4">
                        {Array.from({ length: 6 }).map((_, i) => (
                          <PluginCardSkeleton key={i} />
                        ))}
                      </ul>
                    ) : Object.keys(byCategory).length === 0 ? (
                      <EmptyState
                        title="Nenhum plugin encontrado."
                        hint={search ? `Sem resultados para "${search}".` : 'Catalog vazio — verifique a conexão.'}
                      />
                    ) : (
                      Object.entries(byCategory).map(([category, items]) => (
                        <section key={category}>
                          <h3 className="sticky top-0 z-10 bg-apolo-surface1/95 px-4 py-2 text-[10px] uppercase tracking-wider text-apolo-textTertiary backdrop-blur">
                            {category} ({items.length})
                          </h3>
                          <ul className="space-y-1.5 px-4 pb-4">{items.map(renderCard)}</ul>
                        </section>
                      ))
                    )}
                </div>
              </Tabs.Content>

              <Tabs.Content
                value="installed"
                className="flex-1 overflow-hidden focus:outline-none data-[state=active]:flex data-[state=active]:flex-col"
              >
                <div className="flex-1 overflow-y-auto">
                    {installedCatalog.length === 0 ? (
                      <EmptyState
                        icon={Package}
                        title="Você ainda não habilitou nenhum plugin do catalog."
                        hint="Explore o Catalog →"
                        action={
                          <button
                            type="button"
                            onClick={() => setTab('catalog')}
                            className="mt-2 inline-flex items-center gap-1 rounded-md bg-apolo-accent px-3 py-1.5 text-xs font-medium text-apolo-bg transition hover:bg-apolo-accentHover focus:outline-none focus:ring-2 focus:ring-apolo-accentRing"
                          >
                            Ver Catalog
                          </button>
                        }
                      />
                    ) : (
                      <ul className="space-y-1.5 p-4">{installedCatalog.map(renderCard)}</ul>
                    )}
                    {plugins.length > 0 && (
                      <section className="mx-4 mb-4 mt-2 flex flex-col gap-2 border-t border-apolo-borderSubtle pt-3">
                        <header className="flex items-center justify-between text-xs uppercase tracking-wider text-apolo-textTertiary">
                          <span>Plugins custom instalados</span>
                          {loading && <Loader2 size={11} className="animate-spin" aria-hidden="true" />}
                        </header>
                        <ul className="flex flex-col gap-2">
                          {plugins.map((p) => (
                            <li
                              key={p.id}
                              className="flex items-start justify-between gap-2 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 p-3 text-xs"
                            >
                              <div className="flex flex-col gap-1">
                                <span className="font-medium text-apolo-textPrimary">
                                  {p.name}
                                  {p.version && (
                                    <span className="ml-2 text-apolo-textTertiary">v{p.version}</span>
                                  )}
                                </span>
                                <span className="text-apolo-textTertiary">
                                  <code className="rounded bg-apolo-bg px-1">{p.slug}</code>
                                  {p.description && <span className="ml-2">{p.description}</span>}
                                </span>
                                {p.mcp_server_url && (
                                  <span className="text-apolo-textTertiary">
                                    MCP: <code className="rounded bg-apolo-bg px-1">{p.mcp_server_url}</code>
                                  </span>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => handleDelete(p.id)}
                                aria-label={`Remover ${p.name}`}
                                className="text-apolo-textTertiary transition hover:text-apolo-error"
                              >
                                <Trash2 size={12} aria-hidden="true" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </section>
                    )}
                </div>
              </Tabs.Content>

              <Tabs.Content
                value="by-plugin"
                className="flex-1 overflow-hidden focus:outline-none data-[state=active]:flex data-[state=active]:flex-col"
              >
                <div className="flex-1 overflow-y-auto">
                    {filtered.length === 0 ? (
                      <EmptyState title="Nenhum plugin encontrado." />
                    ) : (
                      <ul className="space-y-1.5 p-4">{filtered.map(renderCard)}</ul>
                    )}
                </div>
              </Tabs.Content>

              {/* E4 — Descobrir: busca + trending no catálogo ClawHub (degrada se cair). */}
              <Tabs.Content
                value="discover"
                className="flex-1 overflow-hidden focus:outline-none data-[state=active]:flex data-[state=active]:flex-col"
              >
                <div className="flex shrink-0 items-center gap-2 border-b border-apolo-borderSubtle p-3">
                  <input
                    type="search"
                    value={clawhubQuery}
                    onChange={(e) => setClawhubQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') loadClawhub(clawhubQuery); }}
                    placeholder="Buscar skills no ClawHub (36k+)…"
                    aria-label="Buscar no ClawHub"
                    className="flex-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-3 py-2 text-xs text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:border-apolo-accent focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => loadClawhub(clawhubQuery)}
                    disabled={clawhubLoading}
                    className="shrink-0 rounded-md bg-apolo-accent px-3 py-2 text-xs font-medium text-apolo-bg transition hover:bg-apolo-accent/90 disabled:opacity-50"
                  >
                    {clawhubLoading ? 'Buscando…' : (clawhubQuery.trim() ? 'Buscar' : 'Trending')}
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                  {clawhubDegraded && (
                    <p className="mb-2 rounded-md border border-apolo-warning/25 bg-apolo-warningBg/40 px-3 py-2 text-[11px] text-apolo-warning">
                      ClawHub indisponível agora — mostrando o que temos em cache (ou vazio). Tente de novo em instantes.
                    </p>
                  )}
                  {clawhubLoading ? (
                    <p className="text-[11px] text-apolo-textTertiary">Carregando catálogo…</p>
                  ) : clawhubItems.length === 0 ? (
                    <EmptyState title="Nada encontrado." hint="Busque por um termo ou veja o Trending." />
                  ) : (
                    <ul className="space-y-1.5">
                      {clawhubItems.map((item) => (
                        <li
                          key={item.slug}
                          className="flex items-start gap-2 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 p-2.5 text-xs"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="truncate font-medium text-apolo-textPrimary">{item.name || item.slug}</span>
                              {item.downloads > 0 && (
                                <span className="shrink-0 rounded bg-apolo-surface3 px-1.5 py-0.5 text-[10px] tabular-nums text-apolo-textTertiary">
                                  ↓ {item.downloads.toLocaleString('pt-BR')}
                                </span>
                              )}
                            </div>
                            {item.description && (
                              <p className="mt-0.5 line-clamp-2 text-[11px] text-apolo-textSecondary">{item.description}</p>
                            )}
                          </div>
                          {item.repo ? (
                            <button
                              type="button"
                              onClick={() => installFromRepo(item.repo)}
                              disabled={installingRepo === item.repo}
                              className="shrink-0 rounded-md border border-apolo-accentBorder bg-apolo-accentMuted px-2.5 py-1.5 text-[11px] font-medium text-apolo-textPrimary transition hover:bg-apolo-accent/20 disabled:opacity-50"
                            >
                              {installingRepo === item.repo ? 'Instalando…' : 'Instalar'}
                            </button>
                          ) : (
                            <a
                              href={`https://clawhub.ai/skills/${encodeURIComponent(item.slug)}`}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="shrink-0 rounded-md border border-apolo-borderSubtle px-2.5 py-1.5 text-[11px] text-apolo-textTertiary transition hover:text-apolo-textPrimary"
                            >
                              Ver no ClawHub
                            </a>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Tabs.Content>

              <Tabs.Content
                value="recents"
                className="flex-1 overflow-hidden focus:outline-none data-[state=active]:flex data-[state=active]:flex-col"
              >
                <div className="flex-1 overflow-y-auto">
                    {recents.length === 0 ? (
                      <EmptyState
                        title="Suas skills mais usadas aparecem aqui."
                        hint="Use uma skill (⌘K para abrir o Command Palette) para começar."
                      />
                    ) : (
                      <ul className="space-y-1 p-4">
                        {recents.map((r) => (
                          <li
                            key={r.skill_id}
                            className="flex items-center justify-between gap-2 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-3 py-2 text-xs"
                          >
                            <div className="flex items-center gap-1.5 min-w-0">
                              <Command size={11} aria-hidden="true" className="text-apolo-textTertiary" />
                              <code className="truncate text-apolo-accent">{r.slash_command || r.skill_id}</code>
                              {r.display_name && (
                                <span className="truncate text-apolo-textTertiary">— {r.display_name}</span>
                              )}
                            </div>
                            <span className="shrink-0 text-[10px] tabular-nums text-apolo-textTertiary">
                              {r.last_used_at ? new Date(r.last_used_at).toLocaleDateString() : ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                </div>
              </Tabs.Content>
            </Tabs.Root>

            {/* E3 — painel de docs por skill: overlay sobre a área das abas */}
            {docSkill && (
              <div className="absolute inset-0 z-10 flex flex-col bg-apolo-surface1">
                <SkillDocPanel skill={docSkill} onBack={() => setDocSkill(null)} />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </dialog>
  );
}
